import { getDefaultLoggingSettings, LoggingSection } from "@multiweb/logging";
import { createDefaultSectionLoggerFactory } from "Logging/defaultLogger";
import { DOMError, IDBRequestException, IDBTransactionException } from "./IndexedDBExceptions";

/**
 * Logger pro `IndexedDBPromise`. Logger se používá ještě před tím, než je k dispozici nastavení logování. (V `ServiceWorkerContext._getConfiguration()`.)
 * Proto si musíme vystačit s výchozím loggerem i nastavením.
 *
 * *POZN*: Výchozí nastavení loguje pouze chyby.
 */
const logger = createDefaultSectionLoggerFactory(getDefaultLoggingSettings(false)).create(LoggingSection.indexedDB);

export class IndexedDBPromise {
    private static _isCompositeKeySupported: boolean | undefined;

    public static get isCompositeKeySupported(): boolean {
        if (IndexedDBPromise._isCompositeKeySupported == null)
            try {
                IndexedDBPromise._isCompositeKeySupported = indexedDB.cmp([1, 1], [1, 2]) === -1;
            } catch (e) {
                IndexedDBPromise._isCompositeKeySupported = false;
            }
        return IndexedDBPromise._isCompositeKeySupported;
    }

    /**
    * otevre DB
    */
    public static openDB(dbName: string): Promise<IDBDatabase> {
        return new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(dbName);
            request.onerror = function(event) {
                reject(new IDBRequestException(`Could not open indexed database '${dbName}'.`, (event.target as IDBOpenDBRequest).error as DOMError)); //nektere prohlizece uvadeji errorCode
            };
            request.onsuccess = function(event) {
                resolve((event.target as IDBRequest).result);
            };
            request.onblocked = () => logger.log("DB BLOCKED IN OPEN");
        });
    }

    /**
    * otevre DB, pokud tato neexistuje zavola metodu oncreate a zalozi struktury - DB struktury neni mozne vytvaret v normalne otevrene DB
    */
    public static createOrOpenDB(dbName: string, oncreate: (db: IDBDatabase) => void, dbVersion?: number): Promise<IDBDatabase> {
        return new Promise<IDBDatabase>((resolve, reject) => {
            const request = dbVersion ? indexedDB.open(dbName, dbVersion) : indexedDB.open(dbName);
            //logger.log("TADY?", request);
            request.onupgradeneeded = function(event) {
                try {
                    const db = (event.target as IDBRequest).result;
                    if (oncreate)
                        oncreate(db);
                } catch (e) {
                    reject(e);
                }
            };
            request.onblocked = () => logger.log("DB BLOCK");
            request.onerror = function(event) {
                reject(new IDBRequestException(`Could not open indexed database '${dbName}'.`, (event.target as IDBOpenDBRequest).error as DOMError)); //nektere prohlizece uvadeji errorCode
            };
            request.onsuccess = function(event) {
                resolve((event.target as IDBRequest).result);
            };
        });
    }

    /**
    * smaze zadanou DB - i pokud neexistuje funkce konci uspesne
    */
    public static deleteDB(dbName: string): Promise<IDBDatabase | undefined> {
        return new Promise<IDBDatabase | undefined>((resolve, reject) => {
            const request = indexedDB.deleteDatabase(dbName);
            request.onerror = function(event) {
                reject(new IDBRequestException(`Could not delete indexed database '${dbName}'.`, (event.target as IDBOpenDBRequest).error as DOMError)); //nektere prohlizece uvadeji errorCode
            };
            request.onsuccess = function(event) {
                resolve((event.target as IDBRequest).result);
            };

            // TODO: Dořešit proč dochází k blokování a záznam se přesto smaže
            request.onblocked = () => {
                logger.log("BLOCK");
                resolve(undefined); /*reject((<IDBOpenDBRequest>event.target))*/
            };
        });
    }

    /**
   * metoda aktualizuje radek
   *
   * @param db databaze
   * @param storeName jmeno store
   * @param indexValue vyhledavana hodnota
   * @param indexName jmeno indexu ve kterem hodnotu hledame - pokud je indexem klic store, tak je jmeno neuvadi dame undefined
   */
    public static updateRow(db: IDBDatabase, storeName: string, indexValue: string, indexName: string | null | undefined, values: { [attrName: string]: any }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const transaction = this._createTransaction(db, storeName, false);
            const objectStore = transaction.objectStore(storeName);

            let request: IDBRequest;
            if (!indexName) {
                request = objectStore.get(indexValue);
            } else {
                const index = objectStore.index(indexName);
                request = index.get(indexValue);
            }

            request.onerror = function(event) {
                reject(new IDBRequestException(`Could not update indexed database '${storeName}'.`, (event.target as IDBOpenDBRequest).error as DOMError)); //nektere prohlizece uvadeji errorCode
            };
            request.onsuccess = function(event) {
                try {
                    const value = (event.target as IDBRequest).result;
                    IndexedDBPromise._updateRow(db, storeName, value, values, resolve, reject);
                } catch (e) {
                    reject(e);
                }
            };
        });
    }

    private static _updateRow<TR>(db: IDBDatabase, storeName: string, row: any, values: { [attrName: string]: any }, resolve: (value?: TR | Promise<TR>) => void, reject: (error?: any) => void): void {
        row = row || {}; // pokud radek nebyl nalezen tak row je undefined;
        const transaction = this._createTransaction(db, storeName, true);
        const objectStore = transaction.objectStore(storeName);
        for (const v of Reflect.ownKeys(values)) {
            if (typeof v === "symbol") continue; // symbol nejde pouzit jako indexer
            row[v] = values[v];
        }
        const request = objectStore.put(row);
        request.onerror = function(event) {
            reject(new IDBRequestException(`Could not update indexed database '${storeName}'.`, (event.target as IDBOpenDBRequest).error as DOMError)); //nektere prohlizece uvadeji errorCode
        };
        request.onsuccess = function() {
            resolve();
        };
    }

    /**
     * Wrapper pro vytvoření transakce. V určitých situacích db.transaction může spadnou vyjimkou, která není zpracována v onerror.
     */
    private static _createTransaction(db: IDBDatabase, storeName: string, readWrite: boolean = false): IDBTransaction {
        try {
            return db.transaction([storeName], readWrite ? "readwrite" : "readonly");
        } catch (ex) {
            if (ex instanceof Error) {
                /*Při vymazani cookie v chrome a kliknutí na akci v toolbaru generuje tuto vyjimku. Navic to je vyjimka, která není zpracována v onerror události objektů IDBDatabase ani IDBTransaction
                  Error: Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing.*/
                if (ex.name && ex.name === "InvalidStateError" && (ex as any).code && (ex as any).code === 11) {
                    const ex1 = new IDBTransactionException(`Failed to create transaction for '${storeName}'.`, ex);
                    ex1.convertToSessionTimeout = true;
                    throw ex1;
                }
                throw new IDBTransactionException(ex.message);
            } else {
                throw new IDBTransactionException();
            }
        }
    }

    /**
    * vraci vsechny radky z daneho store
    */
    public static getAllRows(db: IDBDatabase, storeName: string, orderBy?: { indexName: string; descending: boolean }, maxCount?: number): Promise<any[]> {
        return new Promise((resolve, reject) => {
            const transaction = this._createTransaction(db, storeName, false);
            const objectStore = transaction.objectStore(storeName);

            const rows: any[] = [];
            let count = 0;

            let request: IDBRequest;
            if (orderBy && orderBy.indexName) {
                const index = objectStore.index(orderBy.indexName);
                request = index.openCursor(undefined, orderBy.descending ? "prev" : undefined);
            } else
                request = objectStore.openCursor();
            request.onerror = function(event) {
                reject(new IDBRequestException(`Unable to load data from indexed database '${storeName}'.`, (event.target as IDBOpenDBRequest).error as DOMError)); //nektere prohlizece uvadeji errorCode
            };
            request.onsuccess = function(event) {
                try {
                    const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;
                    if (cursor) {
                        rows.push(cursor.value);
                        count++;
                        if (maxCount && count >= maxCount)
                            resolve(rows);
                        else
                            cursor.continue();
                    } else {
                        resolve(rows);
                    }
                } catch (e) {
                    reject(e);
                }
            };
        });
    }

    /**
     * vraci pocet zaznamu ve store
     */
    public static getRowsCount(db: IDBDatabase, storeName: string): Promise<number> {
        return new Promise<number>((resolve, reject) => {
            const transaction = this._createTransaction(db, storeName, false);
            const objectStore = transaction.objectStore(storeName);

            const request = objectStore.count();
            request.onerror = function(event) {
                reject(new IDBRequestException(`Unable to load data from indexed database '${storeName}'.`, (event.target as IDBOpenDBRequest).error as DOMError)); //nektere prohlizece uvadeji errorCode
            };

            request.onsuccess = function(event) {
                try {
                    resolve((event.target as IDBRequest).result);
                } catch (e) {
                    reject(e);
                }
            };
        });
    }

    /**
    * metoda vrati objekt na zaklade hodnoty z indexu, pokud je index klic store hodnota indexName se uvadi
    *
    * @param db databaze
    * @param storeName jmeno store
    * @param indexValue vyhledavana hodnota
    * @param indexName jmeno indexu ve kterem hodnotu hledame - pokud je indexem klic store, tak je jmeno neuvadi
    */
    public static getRowByIndexValue(db: IDBDatabase, storeName: string, indexValue: any, indexName?: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const transaction = this._createTransaction(db, storeName, false);
            const objectStore = transaction.objectStore(storeName);

            let request: IDBRequest;
            if (!indexName) {
                request = objectStore.get(indexValue);
            } else {
                const index = objectStore.index(indexName);
                request = index.get(indexValue);
            }

            request.onerror = function(event) {
                reject(new IDBRequestException(`Unable to load data from indexed database '${storeName}'.`, (event.target as IDBOpenDBRequest).error as DOMError)); //nektere prohlizece uvadeji errorCode
            };
            request.onsuccess = function(event) {
                const value = (event.target as IDBRequest).result;
                resolve(value);
            };
        });
    }

    /**
    * metoda vrati objekty na zaklade hodnoty z indexu, pokud je index klic store hodnota indexName se uvadi
    *
    * @param db databaze
    * @param storeName jmeno store
    * @param indexValue vyhledavana hodnota
    * @param indexName jmeno indexu ve kterem hodnotu hledame - pokud je indexem klic store, tak je jmeno neuvadi
    */
    public static getRowsByIndexValue(db: IDBDatabase, storeName: string, indexValue: string, indexName?: string): Promise<any[]> {
        return new Promise((resolve, reject) => {
            const transaction = this._createTransaction(db, storeName, false);
            const objectStore = transaction.objectStore(storeName);

            const keyRange = IDBKeyRange.only(indexValue);
            const rows: any[] = [];

            let request: IDBRequest;
            if (!indexName) {
                request = objectStore.openCursor(keyRange);
            } else {
                const index = objectStore.index(indexName);
                request = index.openCursor(keyRange);
            }

            request.onerror = function(event) {
                reject(new IDBRequestException(`Unable to load data from indexed database '${storeName}'.`, (event.target as IDBOpenDBRequest).error as DOMError)); //nektere prohlizece uvadeji errorCode
            };
            request.onsuccess = function(event) {
                try {
                    const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;
                    if (cursor) {
                        rows.push(cursor.value);
                        cursor.continue();
                    } else {
                        resolve(rows);
                    }
                } catch (e) {
                    reject(e);
                }
            };
        });
    }

    public static getRowsByIndexValues(db: IDBDatabase, storeName: string, indexValues: any[], indexName?: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const transaction = this._createTransaction(db, storeName, false);
            const objectStore = transaction.objectStore(storeName);

            const rows: any[] = [];
            let i = 0;
            indexValues = indexValues.sort((a, b) => indexedDB.cmp(a, b)); //konstrukce indexValues.sort(indexedDB.cmp) pada v Chrome 42

            let request: IDBRequest;
            if (!indexName) {
                request = objectStore.openCursor();
            } else {
                const index = objectStore.index(indexName);
                request = index.openCursor();
            }

            request.onerror = function(event) {
                reject(new IDBRequestException(`Unable to load data from indexed database '${storeName}'.`, (event.target as IDBOpenDBRequest).error as DOMError)); //nektere prohlizece uvadeji errorCode
            };

            request.onsuccess = function(event) {
                try {
                    const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;
                    if (!cursor) {
                        resolve(rows);
                        return;
                    }

                    const key = cursor.key;

                    while (indexedDB.cmp(key, indexValues[i]) === 1) {
                        // The cursor has passed beyond this key. Check next.
                        ++i;
                        if (i === indexValues.length) {
                            // There is no next. Stop searching.
                            resolve(rows);
                            return;
                        }
                    }

                    if (indexedDB.cmp(key, indexValues[i]) === 0) {
                        // The current cursor value should be included and we should continue
                        // a single step in case next item has the same key or possibly our
                        // next key in set.
                        rows.push(cursor.value);
                        cursor.continue();
                    } else {
                        // cursor.key not yet at set[i]. Forward cursor to the next key to hunt for.
                        cursor.continue(indexValues[i]);
                    }
                } catch (e) {
                    reject(e);
                }
            };
        });
    }

    /*
    public static async *getRowsByIndexValues(db: IDBDatabase, storeName: string, indexValues: any[], indexName?: string): any { // AsyncIterableIterator<any>
        var transaction = this._createTransaction(db, storeName, false);
        var objectStore = transaction.objectStore(storeName);

        var i = 0;
        indexValues = indexValues.sort((a, b) => indexedDB.cmp(a, b)); //konstrukce indexValues.sort(indexedDB.cmp) pada v Chrome 42

        let cursor = await IndexedDBPromise.openIDBCursorPromise(objectStore, indexName);

        while (!cursor.isDone) {
            const key = cursor.key;

            while (indexedDB.cmp(key, indexValues[i]) === 1) {
                // The cursor has passed beyond this key. Check next.
                ++i;
                if (i === indexValues.length) {
                    // There is no next. Stop searching.
                    return;
                }
            }

            if (indexedDB.cmp(key, indexValues[i]) === 0) {
                // The current cursor value should be included and we should continue
                // a single step in case next item has the same key or possibly our
                // next key in set.
                yield cursor.value;
                cursor = await cursor.continue!();
            } else {
                // cursor.key not yet at set[i]. Forward cursor to the next key to hunt for.
                cursor = await cursor.continue!(indexValues[i]);
            }
        }
    }

    private static _dbRequestReadyPromise = Symbol("dbRequestReadyPromise");

    // převede čekání na výsledek IDBRequestu na Promise
    // @param request ze kterého chceme výsledek
    public static dbRequestReady(request: IDBRequest): Promise<any> {
        var rq = request;
        const rrP = IndexedDBPromise._dbRequestReadyPromise;
        if ((<any>rq)[rrP]) return (<any>rq)[rrP];
        return (<any>rq)[rrP] = new Promise(function (resolve, reject) {
            if (rq.readyState === 'done') {
                if (rq.error)
                    reject(request.error);
                else
                    resolve(request.result);
            } else {
                rq.addEventListener('success', function () { resolve(rq.result); });
                rq.addEventListener('error', function () { reject(rq.error); });
            }
        });
    }

    public static openIDBCursorPromise(objectStore: IDBObjectStore, indexName?: string): Promise<IDBCursorPromise> {
        const request: IDBRequest = !indexName ? objectStore.openCursor() : objectStore.index(indexName).openCursor();
        const reqDef = SyncPromise.defer<IDBCursorPromise>();
        let defToProcess: Deferred<IDBCursorPromise> = reqDef;

        let process = (successValue: any) => {
            let result: IDBCursorPromise = { isDone: !successValue };
            const cursor = <IDBCursorWithValue>successValue;
            if (!result.isDone) {
                result.key = cursor.key;
                result.value = cursor.value;
                result.continue = (key?: IDBKeyRange | IDBValidKey) => {
                    if (!(<any>result)[idbCursorPromiseInternalPromise]) {
                        (<any>result).curDef = defToProcess = SyncPromise.defer<IDBCursorPromise>();
                        cursor.continue(key);
                        return (<any>result)[idbCursorPromiseInternalPromise] = defToProcess.promise;
                    }
                    return (<any>result)[idbCursorPromiseInternalPromise];
                };
            }
            return result;
        };

        let processError = (error: any) => new IDBRequestException(`Unable to load data from indexed database '${objectStore.name}'.`, error);

        if (request.readyState === 'done') {
            if (request.error)
                defToProcess.reject(processError(request.error));
            else
                defToProcess.resolve(process(request.result));
        } else {
            request.onsuccess = (_event) => {
                defToProcess.resolve(process(request.result));
            };
            request.onerror = (_event) => {
                defToProcess.reject(processError(request.error));
            };
        }
        return reqDef.promise;
    }
    */

    /**
     * Smaze obsah zadaneho store
     */
    public static clearStore(db: IDBDatabase, storeName: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (db.objectStoreNames.contains(storeName)) {
                const transaction = this._createTransaction(db, storeName, true);
                const objectStore = transaction.objectStore(storeName);
                const request = objectStore.clear();

                const onerror = function(event: Event) {
                    reject(new IDBRequestException("Failed to clear indexed database store.", (event.target as IDBOpenDBRequest).error as DOMError)); //nektere prohlizece uvadeji errorCode
                };

                request.onerror = onerror.bind(this);
                transaction.onerror = onerror.bind(this);

                transaction.oncomplete = function() {
                    resolve();
                };
            } else {
                resolve();
            }
        });
    }
}

/*
const idbCursorPromiseInternalPromise = Symbol("IDBCursorPromise-Promise");

export interface IDBCursorPromise {
    isDone: boolean;
    value?: any;
    key?: any;
    continue?: (key?: IDBKeyRange | IDBValidKey) => Promise<IDBCursorPromise>;
}
*/
